import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getPendingAllocations, UnifiedTip, calculateAllocations, verifyPOSImport } from '../services/allocationService';
import { approveTipAllocation } from '../services/allocationApprovalService';
import { getSettings, TenantSettings } from '../services/settingsService';
import { getStaff, Staff } from '../services/staffService';
import { getPlatformConfig } from '../services/platformConfigService';
import { useFirebase } from '../contexts/FirebaseContext';
import { CheckCircle, Loader2, Wallet, Calendar, User, ArrowRightLeft, Settings, AlertTriangle, Scale, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

import { PayrollDueReminder } from './PayrollDueReminder';
import { AllocationsHistory } from './AllocationsHistory';
import { formatCurrency } from '../lib/formatters';

interface Props {
  tenantId: string;
}

export const AllocationsDashboard: React.FC<Props> = ({ tenantId }) => {
  const { user } = useFirebase();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') === 'review' ? 'pending' : 'pending';
  
  if (!tenantId) {
    return <div className="p-4 text-red-500">Error: Missing tenant context.</div>;
  }
  
  console.log('AllocationsDashboard: Rendering. Location:', location.pathname, 'InitialTab:', initialTab);
  
  const [activeTab, setActiveTab] = useState<'pending' | 'history'>(initialTab);
  const [tips, setTips] = useState<UnifiedTip[]>([]);
  const [rawSourceTips, setRawSourceTips] = useState<UnifiedTip[]>([]);
  const [rawTipsCount, setRawTipsCount] = useState(0);
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAllocating, setIsAllocating] = useState(false);
  const [status, setStatus] = useState('');
  const [missingPOSContexts, setMissingPOSContexts] = useState<{date: string, shift?: string}[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rawTips, currentSettings, staff] = await Promise.all([
        getPendingAllocations(tenantId),
        getSettings(tenantId),
        getStaff(tenantId)
      ]);
      
      setStaffList(staff);
      setSettings(currentSettings);
      setRawSourceTips(rawTips);
      setRawTipsCount(rawTips.length);
      
      // Determine unique date/shift contexts to check for POS imports
      const contexts = Array.from(new Set(rawTips.map(t => JSON.stringify({ date: t.tipDate, shift: t.shift }))))
        .map(s => JSON.parse(s));
      
      let missing: {date: string, shift?: string}[] = [];
      if (currentSettings.usePosShiftDataForActuals !== false) {
        const posStatus = await verifyPOSImport(tenantId, contexts);
        missing = posStatus.filter(ps => !ps.hasPOS).map(ps => ({ date: ps.date, shift: ps.shift }));
      }
      setMissingPOSContexts(missing);

      const [calculated] = await Promise.all([
        calculateAllocations(tenantId, rawTips)
      ]);
      
      setTips(calculated);
    } catch (error) {
      console.error('Error fetching allocations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tenantId]);

  const handleApprove = async () => {
    if (!user || tips.length === 0) return;
    
    setIsAllocating(true);
    setStatus('');
    try {
      const count = await approveTipAllocation(tenantId, user.email || 'Admin');
      if (count > 0) {
        setStatus(`Successfully allocated ${count} tip(s) for extraction!`);
        await fetchData();
      } else {
        setStatus('No tips were available for allocation.');
      }
    } catch (error) {
      console.error('Error allocating tips:', error);
      let errMsg = 'Failed to allocate tips. Please try again.';
      if (error instanceof Error) {
        try {
          const parsed = JSON.parse(error.message);
          if (parsed && parsed.error) {
            errMsg = parsed.error;
          }
        } catch {
          errMsg = error.message;
        }
      }
      setStatus(errMsg);
    } finally {
      setIsAllocating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-20">
        <Loader2 className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  const totalAmount = tips.reduce((sum, tip) => sum + tip.amount, 0);

  return (
    <div className="space-y-8">
      <PayrollDueReminder />
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-gray-900 tracking-tighter uppercase italic leading-none">Allocations</h1>
          <p className="text-gray-400 font-bold uppercase tracking-widest text-[9px] mt-2">Review & Approve Tip Allocations</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <button 
            onClick={() => fetchData()} 
            disabled={loading}
            className="h-14 sm:h-12 flex items-center justify-center px-4 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-2xl transition-all border border-gray-100 bg-white"
            title="Refresh pending tips"
          >
            <ArrowRightLeft className={`${loading ? 'animate-spin' : ''}`} size={20} />
          </button>
          <button 
            onClick={handleApprove} 
            disabled={tips.length === 0 || isAllocating}
            className="btn-primary h-14 sm:h-12 flex items-center justify-center gap-3 shadow-xl shadow-blue-500/20 text-sm font-black uppercase tracking-widest"
          >
            {isAllocating ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Extracting...
              </>
            ) : (
              <>
                <CheckCircle size={20} />
                Approve Tip Allocations
              </>
            )}
          </button>
        </div>
      </div>

      {status && (
        <div className={`p-4 rounded-xl text-center font-medium animate-in fade-in slide-in-from-top-2 ${
          status.includes('Successfully') ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-700 border border-red-100'
        }`}>
          {status}
        </div>
      )}

      <div className="flex gap-4 border-b border-gray-200">
        <button onClick={() => setActiveTab('pending')} className={`pb-2 px-1 ${activeTab === 'pending' ? 'border-b-2 border-blue-600 font-bold text-blue-600' : 'text-gray-500'}`}>Pending Allocations</button>
        <button onClick={() => setActiveTab('history')} className={`pb-2 px-1 ${activeTab === 'history' ? 'border-b-2 border-blue-600 font-bold text-blue-600' : 'text-gray-500'}`}>History</button>
      </div>

      {activeTab === 'history' ? (
        <AllocationsHistory tenantId={tenantId} />
      ) : (
        <>
          {missingPOSContexts.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 animate-in fade-in slide-in-from-top-4">
              <div className="flex items-center gap-4">
                 <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center text-amber-600 shrink-0">
                    <AlertTriangle size={24} />
                 </div>
                 <div>
                    <h3 className="font-bold text-amber-900">Missing POS Data (Hours/Tracking)</h3>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Missing labor data for: <span className="font-bold">
                        {missingPOSContexts.map(c => `${c.date}${c.shift ? ` (${c.shift})` : ''}`).join(', ')}
                      </span>. 
                      Allocations will default to equal splits for these shifts.
                    </p>
                 </div>
              </div>
              <Link 
                to="/import/pos" 
                className="px-4 py-2 bg-amber-600 text-white text-xs font-bold rounded-xl shadow-lg shadow-amber-600/20 hover:bg-amber-700 transition-all text-center"
              >
                Import POS Data
              </Link>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="card p-6 flex items-center space-x-4">
              <div className="p-3 bg-blue-50 rounded-xl">
                <Wallet className="text-blue-600" size={24} />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Pending Payroll Extract</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalAmount)}</p>
              </div>
            </div>
            <div className="card p-6 flex items-center space-x-4">
              <div className="p-3 bg-purple-50 rounded-xl">
                <ArrowRightLeft className="text-purple-600" size={24} />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Pending Allocation</p>
                <p className="text-2xl font-bold text-gray-900">{rawTipsCount} tip{rawTipsCount !== 1 ? 's' : ''}</p>
                <p className="text-xs font-medium text-gray-500">{formatCurrency(rawSourceTips.reduce((sum, tip) => sum + tip.amount, 0))}</p>
              </div>
            </div>
            <div className="card p-6 flex items-center space-x-4">
              <div className="p-3 bg-emerald-50 rounded-xl">
                <User className="text-emerald-600" size={24} />
              </div>
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Payee Count</p>
                <p className="text-2xl font-bold text-gray-900">{tips.length}</p>
              </div>
            </div>
          </div>

          <div className="card overflow-hidden">
            <div className="p-6 md:p-8 border-b border-gray-50">
              <h2 className="text-xl font-bold text-gray-900">Pending Tips</h2>
              <p className="text-xs text-gray-500 mt-1">Raw tips awaiting allocation processing</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="px-8 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Submitted By & IDs</th>
                    <th className="px-8 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Date</th>
                    <th className="px-8 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Shift</th>
                    <th className="px-8 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Source</th>
                    <th className="px-8 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {rawSourceTips.slice(0, 10).map((tip, index) => {
                    const isPendingPOS = missingPOSContexts.some(c => c.date === tip.tipDate && c.shift === tip.shift);
                    const matchedStaff = staffList.find(s => 
                      (tip.staffId && s.id === tip.staffId) || 
                      (s.email && tip.email && s.email.toLowerCase() === tip.email.toLowerCase()) ||
                      (s.name && tip.staffName && s.name.toLowerCase() === tip.staffName.toLowerCase())
                    );
                    const resolvedPosId = tip.posId || matchedStaff?.posId;
                    const resolvedStaffId = tip.staffId || matchedStaff?.id;
                    const displayName = tip.staffName || matchedStaff?.name || tip.email || 'Unknown Staff';

                    return (
                      <tr key={tip.id || index} className={`group hover:bg-gray-50/50 transition-colors ${isPendingPOS ? 'bg-amber-50/30' : ''}`}>
                        <td className="px-8 py-4 font-semibold text-gray-900">
                          <div className="flex flex-col">
                            <span className="font-bold text-gray-900 text-sm">{displayName}</span>
                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                              {resolvedPosId && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[9px] font-mono font-bold uppercase">
                                  POS ID: {resolvedPosId}
                                </span>
                              )}
                              {resolvedStaffId && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[9px] font-mono font-bold" title={`Full Staff ID: ${resolvedStaffId}`}>
                                  Staff ID: {resolvedStaffId.slice(0, 8)}
                                </span>
                              )}
                              {tip.email && (
                                <span className="text-[10px] text-gray-400 font-medium">
                                  {tip.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-8 py-4 text-gray-600">
                            {tip.tipDate}
                            {isPendingPOS && (
                              <AlertTriangle size={12} className="inline-block text-amber-600 ml-1" />
                            )}
                        </td>
                        <td className="px-8 py-4">
                          <div className="flex items-center gap-2">
                            <span className={`text-gray-600 ${!tip.shift ? 'italic text-gray-400' : ''}`}>
                              {tip.shift || 'General'}
                            </span>
                            {!tip.shift && (
                               <div className="group relative">
                                  <AlertTriangle size={10} className="text-amber-400 cursor-help" />
                                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 p-2 bg-gray-900 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none transition-opacity z-10 font-medium">
                                    No shift assigned. Using "General" split pool.
                                  </div>
                               </div>
                            )}
                          </div>
                        </td>
                        <td className="px-8 py-4">
                          <span className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                            tip.source === 'Cash' ? 'bg-blue-100 text-blue-700' : 
                            tip.source === 'Jar' ? 'bg-purple-100 text-purple-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            {tip.source}
                          </span>
                        </td>
                        <td className="px-8 py-4 text-right font-bold text-gray-900">{formatCurrency(tip.amount)}</td>
                      </tr>
                    );
                  })}
                  {rawSourceTips.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-8 py-20 text-center text-gray-400 font-medium">
                        No pending source tips
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card overflow-hidden">
            <div className="p-6 md:p-8 border-b border-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Calculated Allocations</h2>
                <p className="text-xs text-gray-500 mt-1">Based on your active business rules</p>
              </div>
              
              <Link 
                to="/settings/allocations" 
                className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-bold transition-all hover:shadow-md ${
                  settings?.allocationMethod 
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100' 
                    : 'bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-100 animate-pulse'
                }`}
              >
                <Settings size={16} />
                <span>Method: {settings?.allocationMethod || 'Not Configured'}</span>
              </Link>
            </div>

            <div className="md:hidden divide-y divide-gray-50">
              {tips.length > 0 ? tips.map((tip, index) => {
                const matchedStaff = staffList.find(s => 
                  (tip.staffId && s.id === tip.staffId) || 
                  (s.email && tip.email && s.email.toLowerCase() === tip.email.toLowerCase()) ||
                  (s.name && tip.staffName && s.name.toLowerCase() === tip.staffName.toLowerCase())
                );
                const resolvedPosId = tip.posId || matchedStaff?.posId;
                const resolvedStaffId = tip.staffId || matchedStaff?.id;
                const displayName = tip.staffName || matchedStaff?.name || tip.email || 'Unknown Staff';

                return (
                  <div key={tip.id || index} className="p-5 space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <User size={14} className="text-gray-400" />
                          <span className="font-bold text-gray-900">{displayName}</span>
                        </div>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {resolvedPosId && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px] font-mono font-bold uppercase">
                              POS ID: {resolvedPosId}
                            </span>
                          )}
                          {resolvedStaffId && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-200 text-slate-800 text-[10px] font-mono font-bold" title={`Full Staff ID: ${resolvedStaffId}`}>
                              Staff ID: {resolvedStaffId.slice(0, 8)}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                        tip.source === 'Cash' ? 'bg-blue-100 text-blue-700' : 
                        tip.source === 'Jar' ? 'bg-purple-100 text-purple-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {tip.source === 'Calculated' ? 'Split' : tip.source}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-2 text-xs text-gray-500">
                        <Calendar size={14} />
                        <span>{tip.tipDate} {tip.shift && `• ${tip.shift}`}</span>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-lg font-bold text-gray-900">{formatCurrency(tip.amount)}</span>
                        <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter">
                          {tip.payoutMethod === 'Paycheck' ? 'Paycheck' : `Already Paid (${tip.payoutMethod || 'Cash'})`}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              }) : (
                <div className="p-12 text-center text-gray-400 font-medium">No tips waiting for allocation</div>
              )}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/50">
                    <th className="px-8 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Staff Name & IDs</th>
                    <th className="px-8 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Date</th>
                    <th className="px-8 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Source</th>
                    <th className="px-8 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest">Payout Method</th>
                    <th className="px-8 py-4 font-bold text-gray-400 text-xs uppercase tracking-widest text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {tips.map((tip, index) => {
                    const matchedStaff = staffList.find(s => 
                      (tip.staffId && s.id === tip.staffId) || 
                      (s.email && tip.email && s.email.toLowerCase() === tip.email.toLowerCase()) ||
                      (s.name && tip.staffName && s.name.toLowerCase() === tip.staffName.toLowerCase())
                    );
                    const resolvedPosId = tip.posId || matchedStaff?.posId;
                    const resolvedStaffId = tip.staffId || matchedStaff?.id;
                    const displayName = tip.staffName || matchedStaff?.name || tip.email || 'Unknown Staff';

                    return (
                      <tr key={tip.id || index} className="group hover:bg-gray-50/50 transition-colors">
                        <td className="px-8 py-4 font-semibold text-gray-900">
                          <div className="flex flex-col">
                            <span className="font-bold text-gray-900 text-sm">{displayName}</span>
                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                              {resolvedPosId && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[9px] font-mono font-bold uppercase">
                                  POS ID: {resolvedPosId}
                                </span>
                              )}
                              {resolvedStaffId && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 text-[9px] font-mono font-bold" title={`Full Staff ID: ${resolvedStaffId}`}>
                                  Staff ID: {resolvedStaffId.slice(0, 8)}
                                </span>
                              )}
                              {tip.email && (
                                <span className="text-[10px] text-gray-400 font-medium">
                                  {tip.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-8 py-4 text-gray-600">
                          <div className="flex flex-col">
                            <span>{tip.tipDate}</span>
                            {tip.shift && <span className="text-[10px] text-gray-400 font-medium">{tip.shift}</span>}
                          </div>
                        </td>
                        <td className="px-8 py-4">
                          <span className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                            tip.source === 'Cash' ? 'bg-blue-100 text-blue-700' : 
                            tip.source === 'Jar' ? 'bg-purple-100 text-purple-700' :
                            'bg-emerald-100 text-emerald-700'
                          }`}>
                            {tip.source === 'Calculated' ? 'Split' : tip.source}
                          </span>
                          {tip.masterTxId && (
                            <div className="text-[8px] font-mono text-gray-400 mt-1 uppercase tracking-tighter max-w-[100px] break-all">
                              Parent: {tip.masterTxId}
                            </div>
                          )}
                        </td>
                        <td className="px-8 py-4 text-xs">
                          <span className={`px-2 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider ${
                              tip.payoutMethod === 'Paycheck' ? 'bg-gray-100 text-gray-600' : 'bg-amber-100 text-amber-700'
                          }`}>
                              {tip.payoutMethod === 'Paycheck' ? 'Paycheck' : `Already Paid (${tip.payoutMethod || 'Cash'})`}
                          </span>
                        </td>
                        <td className="px-8 py-4 text-right font-bold text-gray-900">{formatCurrency(tip.amount)}</td>
                      </tr>
                    );
                  })}
                  {tips.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-8 py-20 text-center text-gray-400 font-medium">
                        No tips waiting for allocation
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
